import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Check from '@mui/icons-material/Check';
import { Box, Tooltip } from '@mui/material';
import { CmgStatus } from 'src/redux/reducers/therapyPlanning/getCmgStatusReducer';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

type Props = {
  status?: CmgStatus;
  label?: string;
  weekStart?: string;
  weekEnd?: string;
  planned?: number | null;
  min?: number | null;
  max?: number | null;
};

// The backend speaks LOW / HIGH / NORMAL; the tooltip reads in the planner's own words.
const statusWord: Record<string, string> = {
  LOW: 'under',
  HIGH: 'over',
  NORMAL: 'in range',
};

const RangeStatusIndicator = ({
  status,
  label = 'PT+OT',
  weekStart,
  weekEnd,
  planned,
  min,
  max,
}: Props) => {
  // The backend resolves the weekly verdict; no verdict means there is nothing to show.
  if (!status || !statusWord[status]) return null;

  // e.g. "PT+OT · week 7/19–7/25 · planned 640 min / range 500–600 · over". Each clause is
  // dropped when its data is missing rather than printed as a dash or a wrong number.
  const parts = [label];
  if (weekStart && weekEnd) parts.push(`week ${weekStart}–${weekEnd}`);
  const planLabel = planned != null ? `planned ${planned} min` : '';
  const rangeLabel = min != null && max != null ? `range ${min}–${max}` : '';
  if (planLabel && rangeLabel) parts.push(`${planLabel} / ${rangeLabel}`);
  else if (planLabel || rangeLabel) parts.push(planLabel || rangeLabel);
  parts.push(statusWord[status]);
  const tooltip = parts.join(' · ');

  // NORMAL → green tick; out of range in either direction (LOW or HIGH) → red arrow.
  const icon =
    status === 'NORMAL' ? (
      <Check sx={{ fontSize: 20, color: '#12B76A' }} />
    ) : status === 'LOW' ? (
      <ArrowDownwardIcon sx={{ fontSize: 20, color: '#F04438' }} />
    ) : (
      <ArrowUpwardIcon sx={{ fontSize: 20, color: '#F04438' }} />
    );

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      arrow
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'default', lineHeight: 0 }}>
        {icon}
      </Box>
    </Tooltip>
  );
};

export default RangeStatusIndicator;